import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Package, ShoppingCart, DollarSign, Clock, Search, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Header } from "@/components/site/Header";
import { Seo } from "@/components/seo/Seo";
import { JournalAdmin } from "@/components/admin/JournalAdmin";
import { KeywordsAdmin } from "@/components/admin/KeywordsAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "overview",  label: "Overview" },
  { id: "customers", label: "Customers" },
  { id: "orders",    label: "Orders" },
  { id: "journal",   label: "Journal" },
  { id: "keywords",  label: "Keywords" },
];

const ORDER_STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];

export const AdminDashboard = () => {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ready && (!user || user.role !== "admin")) nav("/login");
  }, [ready, user, nav]);

  const load = async () => {
    if (!user || user.role !== "admin") return;
    setLoading(true);
    try {
      const [s, c, o] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/customers`),
        axios.get(`${API}/admin/orders`),
      ]);
      setStats(s.data);
      setCustomers(c.data.customers || []);
      setOrders(o.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  // Customer search (debounced)
  useEffect(() => {
    if (tab !== "customers") return;
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/customers`, {
          params: customerSearch ? { q: customerSearch } : {},
        });
        setCustomers(data.customers || []);
      } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(handle);
  }, [customerSearch, tab]);

  const updateOrderStatus = async (oid, status) => {
    await axios.put(`${API}/admin/orders/${oid}/status`, { status });
    load();
  };

  if (!user || user.role !== "admin") return null;

  return (
    <>
      <Seo title="Admin · Operations" path="/admin" noindex />
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 pt-28 md:pt-32 pb-24" data-testid="admin-dashboard">
        <div className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3 font-medium">Admin · {user.email}</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight leading-[1.02] mb-10">
          <span className="font-black">Operations</span> <span className="font-thin">control.</span>
        </h1>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-neutral-200 mb-10 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`pb-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${tab === t.id ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"}`}
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <section data-testid="admin-overview">
            {!stats ? <div className="text-neutral-500">Loading…</div> : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-neutral-200 border border-neutral-200">
                <Stat icon={Package} label="Products" value={stats.total_products} testid="stat-products" />
                <Stat icon={Users} label="Customers" value={stats.total_customers} testid="stat-customers" />
                <Stat icon={ShoppingCart} label="Orders" value={stats.total_orders} testid="stat-orders" />
                <Stat icon={Clock} label="Pending orders" value={stats.pending_orders} testid="stat-pending-orders" />
                <Stat icon={DollarSign} label="Revenue" value={`$${stats.revenue_total.toFixed(2)}`} testid="stat-revenue" />
              </div>
            )}
          </section>
        )}

        {tab === "customers" && (
          <section data-testid="admin-customers">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="font-heading text-2xl font-black tracking-tight">Customer accounts ({customers.length})</h2>
              <div className="flex items-center gap-1 border border-neutral-300 px-2">
                <Search className="w-3.5 h-3.5 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search email, name, clinic…"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="px-2 py-1.5 text-xs outline-none w-56"
                  data-testid="customer-search"
                />
              </div>
            </div>
            {loading ? (
              <div className="py-12 flex items-center justify-center text-neutral-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
            ) : customers.length === 0 ? (
              <p className="text-sm text-neutral-500" data-testid="customers-empty">
                No customers yet. Accounts are created automatically when guests opt to save their info during Stripe checkout.
              </p>
            ) : (
              <div className="border border-neutral-200 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50">
                    <tr className="text-left">
                      <Th>Customer</Th><Th>Email</Th><Th>Country</Th><Th>Orders</Th><Th>Spent</Th><Th>Last order</Th><Th>Registered</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {customers.map((c) => (
                      <tr key={c.id} data-testid={`customer-row-${c.id}`} className="hover:bg-neutral-50">
                        <Td>
                          <div className="font-bold">{c.name || c.email}</div>
                          {c.clinic_name && <div className="text-xs text-neutral-500">{c.clinic_name}</div>}
                        </Td>
                        <Td className="text-xs">{c.email}</Td>
                        <Td className="text-xs">{c.country || "—"}</Td>
                        <Td className="text-xs font-bold tabular-nums">{c.orders || 0}</Td>
                        <Td className="text-xs font-bold tabular-nums">${(c.spent || 0).toFixed(2)}</Td>
                        <Td className="text-xs text-neutral-500">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString() : "—"}</Td>
                        <Td className="text-xs text-neutral-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === "orders" && (
          <section data-testid="admin-orders">
            <h2 className="font-heading text-2xl font-black tracking-tight mb-4">Orders ({orders.length})</h2>
            {orders.length === 0 ? <p className="text-sm text-neutral-500">No orders yet.</p> : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="border border-neutral-200 p-5" data-testid={`order-row-${o.id}`}>
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div>
                        <div className="font-heading font-black tracking-tight">{o.order_number}</div>
                        <div className="text-xs text-neutral-500">{new Date(o.created_at).toLocaleString()} · {o.user_email}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold">${o.total.toFixed(2)}</span>
                        <select
                          value={o.status}
                          onChange={(e) => updateOrderStatus(o.id, e.target.value)}
                          className="border border-neutral-300 px-3 py-1.5 text-sm focus:outline-none focus:border-black"
                          data-testid={`order-status-${o.id}`}
                        >
                          {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-600">
                      {o.clinic_name && <><strong>{o.clinic_name}</strong> · </>}{o.shipping?.city}, {o.shipping?.country} · {o.items.length} item{o.items.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "journal" && <JournalAdmin />}
        {tab === "keywords" && <KeywordsAdmin />}
      </main>
    </>
  );
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white p-6" data-testid={testid}>
    <Icon className="w-5 h-5 mb-3 text-neutral-400" strokeWidth={1.75} />
    <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{label}</div>
    <div className="font-heading text-3xl font-black tracking-tight mt-1">{value}</div>
  </div>
);

const Th = ({ children }) => <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
